import { EquipmentSlot } from '../atoms/EquipmentSlot';
import { useMemo }        from 'react';
import playerDownPng      from '../../assets/player/player-down.png';

/**
 * Classic RPG paper-doll equipment layout.
 *
 * Grid (3 cols × 4 rows):
 *   col →   1          2          3
 *  row ↓
 *   1               [Helmet]
 *   2     [Weapon]  [PLAYER]   [Armor]
 *   3     [Ring]    [PLAYER]   [Gloves]
 *   4     [Ring]    [Boots]    [Amulet]
 *
 * Props:
 *  equipped   — equipped map
 *  onUnequip  — callback(slotId)
 *  view       — 'items' (default) | 'runes'
 *               In rune view the same grid positions are occupied by per-slot
 *               rune sockets (helmet_rune, weapon_rune, …) instead of gear slots.
 */
const SLOTS = [
  { id: 'helmet', label: 'Helmet',  icon: '🪖', gridColumn: 2, gridRow: 1 },
  { id: 'weapon', label: 'Weapon',  icon: '⚔️', gridColumn: 1, gridRow: 2 },
  { id: 'armor',  label: 'Armor',   icon: '🛡️', gridColumn: 3, gridRow: 2 },
  { id: 'ring1',  label: 'Ring',    icon: '💍', gridColumn: 1, gridRow: 3 },
  { id: 'gloves', label: 'Gloves',  icon: '🧤', gridColumn: 3, gridRow: 3 },
  { id: 'ring2',  label: 'Ring',    icon: '💍', gridColumn: 1, gridRow: 4 },
  { id: 'boots',  label: 'Boots',   icon: '👢', gridColumn: 2, gridRow: 4 },
  { id: 'amulet', label: 'Amulet',  icon: '📿', gridColumn: 3, gridRow: 4 },
];

// Mirrors SLOTS exactly — same grid positions, rune-socket IDs and labels
const RUNE_SLOTS = [
  { id: 'helmet_rune', label: 'Helmet',  icon: '🔮', gridColumn: 2, gridRow: 1 },
  { id: 'weapon_rune', label: 'Weapon',  icon: '🔮', gridColumn: 1, gridRow: 2 },
  { id: 'armor_rune',  label: 'Armor',   icon: '🔮', gridColumn: 3, gridRow: 2 },
  { id: 'ring1_rune',  label: 'Ring',    icon: '🔮', gridColumn: 1, gridRow: 3 },
  { id: 'gloves_rune', label: 'Gloves',  icon: '🔮', gridColumn: 3, gridRow: 3 },
  { id: 'ring2_rune',  label: 'Ring',    icon: '🔮', gridColumn: 1, gridRow: 4 },
  { id: 'boots_rune',  label: 'Boots',   icon: '🔮', gridColumn: 2, gridRow: 4 },
  { id: 'amulet_rune', label: 'Amulet',  icon: '🔮', gridColumn: 3, gridRow: 4 },
];

export function PaperDoll({ equipped = {}, onUnequip, view = 'items' }) {
  const activeSlots = view === 'runes' ? RUNE_SLOTS : SLOTS;

  // Build a map of { setId: count } from currently equipped items (main slots only)
  const setCountMap = useMemo(() => {
    const map = {};
    const mainSlots = ['weapon', 'helmet', 'armor', 'ring1', 'ring2', 'gloves', 'boots', 'amulet'];
    for (const slotId of mainSlots) {
      const item = equipped[slotId];
      if (item?.setId) {
        map[item.setId] = (map[item.setId] ?? 0) + 1;
      }
    }
    return map;
  }, [equipped]);

  return (
    <div className={`paper-doll-wrapper${view === 'runes' ? ' paper-doll-wrapper--runes' : ''}`}>
      <div className="paper-doll">
        {activeSlots.map(slot => {
          const item = equipped[slot.id] ?? null;
          return (
            <EquipmentSlot
              key={slot.id}
              id={slot.id}
              label={slot.label}
              icon={slot.icon}
              gridColumn={slot.gridColumn}
              gridRow={slot.gridRow}
              item={item}
              onUnequip={onUnequip}
              equippedSetCount={item?.setId ? (setCountMap[item.setId] ?? 0) : undefined}
            />
          );
        })}

        {/* Player figure occupies col 2, rows 2–3 */}
        <div className="player-figure" style={{ gridColumn: 2, gridRow: '2 / span 2' }}>
          <img src={playerDownPng} alt="Player" className="player-fig-sprite" />
        </div>
      </div>
    </div>
  );
}
